import React from 'react'
import { StatusPill } from '../../Common'
import { useNls } from '../../../nls/nlsProvider'
import type { OpsRecordCoSign, OpsTemplateCoSignConfig } from '../../../types/ops'

export interface CoSignBadgeProps {
  coSign: OpsRecordCoSign
  config?: OpsTemplateCoSignConfig | null
  signerName?: string
  proxyAuthorName?: string
}

export const CoSignBadge: React.FC<CoSignBadgeProps> = ({
  coSign,
  config,
  signerName,
  proxyAuthorName,
}) => {
  const { t } = useNls()

  const label = config?.label || t('NLSOpsRegisterCoSignDefaultLabel')
  const name = signerName || (coSign.signerRef.kind === 'MEMBER' ? coSign.signerRef.uid : coSign.signerRef.workerId) || ''

  switch (coSign.state) {
    case 'AWAITING': {
      return (
        <div className="opsRegistersCoSignBadgeGroup" data-testid="cosign-badge-awaiting">
          <StatusPill tone="info">
            {label}: {t('NLSOpsRegisterCoSignStateAwaiting')}
          </StatusPill>
          {name && (
            <span className="opsRegistersFieldHint">
              {t('NLSOpsRegisterCoSignSignerNotice', { name })}
            </span>
          )}
        </div>
      )
    }

    case 'SIGNED': {
      return (
        <div className="opsRegistersCoSignBadgeGroup" data-testid="cosign-badge-signed">
          <StatusPill tone="success">
            {label}: {t('NLSOpsRegisterCoSignStateSigned')}
          </StatusPill>
          <span className="opsRegistersFieldHint">
            {t('NLSOpsRegisterCoSignSignerNotice', {
              name: coSign.signedBy || name,
            })}
          </span>
        </div>
      )
    }

    case 'DISPUTED': {
      return (
        <div className="opsRegistersCoSignBadgeGroup" data-testid="cosign-badge-disputed">
          <StatusPill tone="danger">
            {label}: {t('NLSOpsRegisterCoSignStateDisputed')}
          </StatusPill>
          <span className="opsRegistersFieldHint">
            {t('NLSOpsRegisterCoSignDisputeNotice', {
              name: coSign.disputedBy || name,
              reason: coSign.disputeReason || '',
            })}
          </span>
        </div>
      )
    }

    case 'PROXY_SIGNED': {
      return (
        <div className="opsRegistersCoSignBadgeGroup" data-testid="cosign-badge-proxysigned">
          <StatusPill tone="success">
            {label}: {t('NLSOpsRegisterCoSignStateProxySigned')}
          </StatusPill>
          <span className="opsRegistersFieldHint">
            {t('NLSOpsRegisterCoSignProxyNotice', {
              name: proxyAuthorName || coSign.proxySignedBy || '',
              reason: coSign.proxyReason || '',
            })}
          </span>
        </div>
      )
    }

    default:
      return null
  }
}
